import React, { useState } from 'react';
import { Box, IconButton, List, ListItem, Popover, Stack, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';

// Line item types that carry an upgrade behind them.
const UPGRADE_TYPES = ['package', 'upsell'];

/**
 * Content of an upgrade in the order it should be read: the benefits list is what the
 * catalog maintains for the booking widget and the most useful answer at the counter;
 * the long description and the short one only stand in where no benefits exist.
 */
const pickContent = details => {
  const benefits = Array.isArray(details.benefits) ? details.benefits : [];
  if (benefits.length > 0) return { benefits, text: '' };
  return { benefits: [], text: details.description || details.descriptionShort || '' };
};

/**
 * Explains what a package or upsell contains, next to its description in the line items
 * table. Renders nothing for line items without an upgrade or without describable
 * content, so the glyph appearing is itself the signal that there is something to read.
 *
 * Uses a question mark rather than the ⓘ the same row already spends on the payment
 * details column. Opens on click rather than hover: a hover popover has to opt out of
 * pointer events to survive the mouse leaving the trigger, and that same opt-out stops
 * the backdrop from ever closing it again on a touch device.
 */
function UpgradeContentHint({ item }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const details = item?.upgradeDetails;
  if (!details || !UPGRADE_TYPES.includes(item?.type)) return null;

  const { benefits, text } = pickContent(details);
  if (benefits.length === 0 && !text) return null;

  const title = details.name || item.description || 'Upgrade';
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size='small'
        aria-label={`Was ist in "${title}" enthalten?`}
        onClick={event => setAnchorEl(open ? null : event.currentTarget)}
        sx={{ color: 'text.secondary' }}
      >
        <HelpOutlineIcon fontSize='small' />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 340 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            {title}
          </Typography>
          {benefits.length > 0 ? (
            <List dense disablePadding>
              {benefits.map((benefit, index) => (
                <ListItem key={`${benefit}-${index}`} disableGutters sx={{ py: 0.25 }}>
                  <Stack direction='row' spacing={1} alignItems='flex-start'>
                    <CheckIcon fontSize='small' color='success' sx={{ mt: '2px' }} />
                    <Typography variant='body2'>{benefit}</Typography>
                  </Stack>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
              {text}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default UpgradeContentHint;
